import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
  TableSortLabel,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { mareOwnersAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { US_STATES } from '../constants/usStates';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

// Phone number formatting function
const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if it's a valid US phone number (10 digits)
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Check if it's a valid US phone number with country code (11 digits starting with 1)
  if (cleaned.length === 11 && cleaned[0] === '1') {
    const withoutCountryCode = cleaned.slice(1);
    return `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`;
  }
  
  // Return original if it doesn't match expected formats
  return phone;
};

const MareOwnersPage = () => {
  const notify = useNotify();
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
  });

  const loadOwners = useCallback(async () => {
    setLoadError('');
    try {
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      const response = await mareOwnersAPI.getAll(params);
      const raw = response?.data;
      if (raw != null && !Array.isArray(raw)) {
        console.warn('Mare owners API returned non-array payload; using empty list', raw);
      }
      setOwners(Array.isArray(raw) ? raw : []);
    } catch (error) {
      console.error('Error loading mare owners:', error);
      // The previous list is left in place on purpose: a failed refresh should say so
      // rather than blank the page out into something indistinguishable from "no owners".
      setLoadError(error?.response?.data?.error || 'Unable to load mare owners. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    loadOwners();
  }, [loadOwners]);

  useRefreshOnFocus(loadOwners);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleOpenDialog = (owner = null) => {
    if (owner) {
      setEditingOwner(owner);
      setFormData(owner);
    } else {
      setEditingOwner(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOwner(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (editingOwner) {
        await mareOwnersAPI.update(editingOwner.id, formData);
      } else {
        await mareOwnersAPI.create(formData);
      }
      handleCloseDialog();
      loadOwners();
    } catch (error) {
      console.error('Error saving mare owner:', error);
      notify.error(errorMessage(error, 'Error saving mare owner'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await mareOwnersAPI.delete(deleteTarget);
      setDeleteTarget(null);
      loadOwners();
    } catch (error) {
      console.error('Error deleting mare owner:', error);
      notify.error(errorMessage(error, 'Error deleting mare owner'));
    }
  };


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-start', sm: 'space-between' },
          alignItems: 'center',
          gap: { xs: 1.5, sm: 0 },
          mb: 3,
        }}
      >
        <Typography variant="h4">Mare Owners</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            minWidth: { xs: 0, sm: 'auto' },
            width: { xs: 32, sm: 'auto' },
            height: { xs: 32, sm: 'auto' },
            px: { xs: 0, sm: 2 },
            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Add Mare Owner
          </Box>
        </Button>
      </Box>
      {loadError ? (
        <Alert severity="error" sx={{ mb: 0.5 }}>
          {loadError}
        </Alert>
      ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortOrder : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'phone'}
                  direction={sortBy === 'phone' ? sortOrder : 'asc'}
                  onClick={() => handleSort('phone')}
                >
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'address'}
                  direction={sortBy === 'address' ? sortOrder : 'asc'}
                  onClick={() => handleSort('address')}
                >
                  Street Address
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'city'}
                  direction={sortBy === 'city' ? sortOrder : 'asc'}
                  onClick={() => handleSort('city')}
                >
                  City, State
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'zip_code'}
                  direction={sortBy === 'zip_code' ? sortOrder : 'asc'}
                  onClick={() => handleSort('zip_code')}
                >
                  Zip Code
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={7} />}
            {(owners ?? []).map((owner) => (
              <TableRow key={owner.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(owner) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(owner.id), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{owner.name}</TableCell>
                <TableCell>{owner.email || '-'}</TableCell>
                <TableCell>{formatPhoneNumber(owner.phone)}</TableCell>
                <TableCell>{owner.address || '-'}</TableCell>
                <TableCell>
                  {owner.city && owner.state ? `${owner.city}, ${owner.state}` : '-'}
                </TableCell>
                <TableCell>{owner.zip_code || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingOwner ? 'Edit Mare Owner' : 'Add New Mare Owner'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="name"
                  label="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="phone"
                  label="Phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="address"
                  label="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="city"
                  label="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  options={US_STATES}
                  getOptionLabel={(option) => option.name}
                  filterOptions={createPrefixFilter((option) => option.name)}
                  value={US_STATES.find(state => state.code === formData.state) || null}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, state: newValue ? newValue.code : '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      label="State"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        {option.code} - {option.name}
                      </Box>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option.code === value?.code}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  name="zip_code"
                  label="Zip Code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingOwner ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Mare Owner"
        message="Permanently delete this mare owner? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default MareOwnersPage;

